import React, { useState } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import ZionForm, { FormTextField } from '@fs/zion-form'
import {
  Paragraph,
  Cell,
  DialogOverlay,
  DialogOverlayActions,
  Grid,
  useContainerWidth,
  useStatusOverlay,
  Separator,
} from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { yupObject as object, verificationCodeSchema as verificationCode } from '@fs/identity.js'
import CancelButton from './CancelButton'
import SubmitButton from './SubmitButton'
import { useErrorReporter, useZionForm } from '../../hooks'
import { ValidationError } from '../../errors'
import { fieldNames } from '../../constants'

const columnSizes = {
  default: 12,
  // wide enough to put validation error on one line
  sm: 8,
}

const code = fieldNames.verificationCode

const defaultValues = {
  [code]: '',
}

const validationSchema = object().shape({
  [code]: verificationCode,
})

export default function BaseVerifyMobileDialog({
  Tag = DialogOverlay,
  overlay,
  messageText,
  resendSmsVerification,
  verifySmsCode,
  onVerified,
  onSubmit,
  hideCancelButton = false,
  onClose,
}) {
  const { t } = useTranslation()
  const reportError = useErrorReporter()
  const showStatusOverlay = useStatusOverlay()
  const [loading, setLoading] = useState(false)
  const atWidth = useContainerWidth()
  const columns = atWidth(columnSizes)

  const close = () => {
    onClose?.()
    overlay?.close()
  }

  const resendCode = async () => {
    try {
      setLoading(true)
      await resendSmsVerification()
      showStatusOverlay({
        message: t('code-resent.feedback', 'A verification code was successfully resent via text message.'),
        type: 'success',
      })
    } catch (error) {
      reportError(error)
      close()
    } finally {
      setLoading(false)
    }
  }

  const methods = useZionForm({
    mode: 'onSubmit',
    resolver: yupResolver(validationSchema),
    defaultValues,
  })

  const {
    reset,
    formState: { isSubmitting },
    setError,
  } = methods

  const handleSubmit = async (values) => {
    try {
      await verifySmsCode(values[code])
      onVerified?.()
      showStatusOverlay({
        message: t('mobile-phone-number-verified.feedback', 'Mobile number was verified successfully.'),
        type: 'success',
      })
      if (onSubmit) {
        await onSubmit()
      } else {
        close()
      }
    } catch (error) {
      if (error instanceof ValidationError) {
        setError(code, { type: 'manual', message: error.render() }, { shouldFocus: true })
      } else {
        reportError(error)
      }
    }
  }

  const headingText = t('check-text-messages.heading', 'Check Text Messages')

  return (
    <Tag title={headingText} {...overlay} as={ZionForm} methods={methods} onSubmit={handleSubmit}>
      <Paragraph size="sm">{messageText}</Paragraph>
      <Grid>
        <Cell columns={columns}>
          <Separator size="xs" />
          <FormTextField
            id="code"
            autoComplete="off"
            name={code}
            label={t('verification-code.label', 'Verification Code')}
            placeholder={t('enter-verification-code.placeholder', 'Enter verification code')}
          />
        </Cell>
      </Grid>

      <DialogOverlayActions wrap>
        {!hideCancelButton && (
          <CancelButton
            onClick={() => {
              close()
              reset()
            }}
          />
        )}
        <SubmitButton
          emphasis="low"
          type="button"
          id="resend-code-button"
          onClick={resendCode}
          disabled={isSubmitting || loading}
          isSubmitting={loading}
        >
          {t('resend-code.action', 'Resend Code')}
        </SubmitButton>
        <SubmitButton type="submit" disabled={isSubmitting || loading} isSubmitting={isSubmitting}>
          {t('verify.action', 'Verify')}
        </SubmitButton>
      </DialogOverlayActions>
    </Tag>
  )
}
